import { takeLatest, call, put, select } from 'redux-saga/effects';
import { takeAuth } from '../auth';
import { OK } from 'http-status-codes';

import { types, actions } from './duck';
import { getCustomerProfile, saveFcmToken } from './api';

const HAS_FREE_PLAN = 'has_free_plan';

// NOTE: mobile uses @react-native-async-storage/async-storage for this.
// localStorage is the direct web equivalent — same get/set/remove-by-key
// shape, synchronous (fine to `call` from a saga either way).
//
// FIX: `state.auth.login.credentails` (yes, that's the existing key name —
// it appears to be a pre-existing typo for "credentials", left as-is here
// rather than renamed, since changing it would need to happen everywhere
// else it's referenced too) can be `null` — confirmed via logging that this
// is exactly what happens on the SignIn (customer) login path, where
// destructuring `{ email }` off `null` throws and aborts the ENTIRE
// getCustomerProfileSaga try block before it ever reaches
// `getCustomerProfileSuccess`. That's why the profile fetch appeared to
// silently fail even though the actual API call succeeded with real data —
// this helper crashed before the success action was ever dispatched.
// Guarded with a fallback so a missing/null credentials object can't take
// down the whole profile fetch.
function* getFreePlanKey() {
  const credentials = yield select(state => state.auth.login.credentails);
  const email = (credentials && credentials.email) || 'anonymous';
  return `${email}/${HAS_FREE_PLAN}`;
}

export function* getCustomerProfileSaga() {
  try {
    yield put(actions.getCustomerProfileRequest());
    const response = yield call(getCustomerProfile);

    const freePlanKey = yield call(getFreePlanKey);

    /* eslint-disable camelcase */
    const has_free_plan = yield call([localStorage, 'getItem'], freePlanKey);

    if (response.status === OK) {
      yield put(
        actions.getCustomerProfileSuccess({
          ...response.data,
          has_free_plan,
        }),
      );
    } else {
      const errorMessage =
        (response.data && response.data.detail) || response.data || response.status;

      throw new Error(errorMessage);
    }
  } catch (error) {
    yield put(actions.getCustomerProfileFailure(error.message));
  }
}

export function* setFreePlanActiveSaga({ value }) {
  const freePlanKey = yield call(getFreePlanKey);
  yield call([localStorage, 'setItem'], freePlanKey, value);
  yield put(actions.getCustomerProfile());
}

export function* setFreePlanDisabledSaga() {
  const freePlanKey = yield call(getFreePlanKey);
  yield call([localStorage, 'removeItem'], freePlanKey);
  yield put(actions.getCustomerProfile());
}

// FCM token saving — relevant once push notifications are wired up on web.
export function* saveFcmTokenSaga({ token }) {
  try {
    yield put(actions.saveFcmTokenRequest());
    const response = yield call(saveFcmToken, token);
    if (response.status === OK) {
      yield put(actions.saveFcmTokenSuccess());
    } else {
      const errorMessage =
        (response.data && response.data.detail) || response.data || response.status;

      throw new Error(errorMessage);
    }
  } catch (error) {
    yield put(actions.saveFcmTokenFailure(error.message));
  }
}

function* initialize() {
  yield put(actions.getCustomerProfile());
}

export default function* saga() {
  yield takeLatest(types.GET_CUSTOMER_PROFILE, getCustomerProfileSaga);
  yield takeLatest(types.SET_FREE_PLAN_ACTIVE, setFreePlanActiveSaga);
  yield takeLatest(types.SET_FREE_PLAN_DISABLED, setFreePlanDisabledSaga);
  yield takeLatest(types.SAVE_FCM_TOKEN, saveFcmTokenSaga);
  yield takeAuth(initialize);
}